import {api, RequestParam} from "@/shared/api";
import * as Input from "@/shared/utils/input";

import {BasicDataTable, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import {DownloadStatus} from "@/shared/ui/downloadStatus";
import {
	DashboardTable,
	DashboardTableBarPresentation,
	DashboardTableConfig,
	FooterDefinition,
	registerSortingFunctions
} from "@/features/reports/dashboardTable";
import {ButtonRadioGroup} from "@/shared/ui/buttonRadioGroup";
import {BudgetDashboardTaskDropdown} from "@/features/reports/budgetDashboardTaskDropdown";
import {UserSettingsChanger} from "@/shared/userSettingsChanger";
import {Combobox} from "@/shared/ui/combobox";
import {DashboardFilterPath} from "@/features/reports/dashboardFilterPath";
import {TaskDropdown} from "@/shared/task/taskDropdown";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {formatPercent, mergeProperties} from "@/shared/utils/misc";
import {
	formatCurrency,
	formatDate,
	formatDuration,
	getCurrentISOOffset,
	getISODate,
	parseDateInput,
	Temporal
} from "@/shared/utils/datefunctions";
import {
	BudgetDashboardTaskResponse,
	BudgetDashboardUserResponse,
	DashboardBudgetSumEntry,
	FilterUsersResponse,
	Task,
	User
} from "@/shared/timr";
import {ExportType, TaskStatusFilter} from "@/shared/timrTypes";
import {renderProgress} from "@/features/reports/progressBarHelper";

export enum BudgetDashboardType {
	HOURS, REVENUE, COSTS
}

export enum BudgetDashboardMode {
	PERSONAL, TASK_LEADER
}

export interface BudgetDashboardConfig {
	defaultUserId: number;
	isTaskLeader: boolean;
}

class BudgetDashboardState {
	public static readonly EVENT_CHANGE_TASK = "changeTask";

	private currentTaskId: number = null;

	public initializeTask(taskId: number) {
		this.currentTaskId = taskId;
	}

	public changeTask(taskId: number) {
		if (this.currentTaskId !== taskId) {
			this.currentTaskId = taskId;
			$(this).triggerHandler(BudgetDashboardState.EVENT_CHANGE_TASK, taskId);
		}
	}

	public resetTask() {
		this.currentTaskId = null;
		$(this).triggerHandler(BudgetDashboardState.EVENT_CHANGE_TASK, null);
	}

	public getTaskId(): number {
		return this.currentTaskId;
	}

	public onTaskChanged(handler: (taskId: number) => void) {
		$(this).on(BudgetDashboardState.EVENT_CHANGE_TASK, (eventObject, args: number) => handler(args));
	}
}

export class BudgetDashboardPage {
	public static GROUP_BUDGET_DASHBOARD = "budgetDashboard";

	private readonly dashboardState: BudgetDashboardState;

	private readonly budgetDashboardTaskTable: TaskTable;
	private readonly budgetDashboardUserTable: UserTable;

	private readonly dashboardFilterPath: DashboardFilterPath;

	private readonly userCombobox: Combobox<User>;
	private readonly tablePresentationControl: ButtonRadioGroup<DashboardTableBarPresentation>;
	private readonly tableTypeControl: ButtonRadioGroup<BudgetDashboardType>;
	private readonly tableModeControl: ButtonRadioGroup<BudgetDashboardMode>;
	private taskDropdown: BudgetDashboardTaskDropdown;
	private currentTaskExternalComments: string = null;

	constructor(config: BudgetDashboardConfig) {
		this.dashboardState = new BudgetDashboardState();
		const selectedTask = BrowserStorageSettings.getSelectedTask(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD)
		if (selectedTask) {
			this.dashboardState.initializeTask(selectedTask.id)
		}

		this.dashboardState.onTaskChanged(() => {
			this.budgetDashboardTaskTable.filter(this.buildFilterParams());
			if (this.tableModeControl && this.tableModeControl.getValue() === BudgetDashboardMode.TASK_LEADER) {
				this.reloadUsers(BrowserStorageSettings.getBudgetDashboardUserFilter(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD));
				this.budgetDashboardUserTable.filter(this.buildFilterParams());
			}
		});

		$(".reload-table").click(() => {
			this.updateBudgetDashboardTables();
			this.reloadUsers(BrowserStorageSettings.getBudgetDashboardUserFilter(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD));
		});

		this.dashboardFilterPath = new DashboardFilterPath({
			currentFilterPathElement: $("#filterPath"),
			onFilterPathElementClick: (_e, taskId: number) => {
				this.dashboardState.changeTask(taskId);
				// reset Task Dropdown if navigated via Task Breadcrumbs
				// we can't set the Task here as we have only the ID and there is no easy option right now IMHO
				this.taskDropdown.setSelectedTask();
				BrowserStorageSettings.setSelectedTask(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD, null);
			},
			currentFilterPathRootMessage: messages["report.rootTask"]
		});

		this.tablePresentationControl = ButtonRadioGroup.typed({
			value: DashboardTableBarPresentation.Absolute,
			button: document.getElementById("dashboardAbsoluteButton")
		}, {
			value: DashboardTableBarPresentation.Relative,
			button: document.getElementById("dashboardRelativeButton")
		}).onValueChanged(presentation => {
			new UserSettingsChanger("budgetDashboardPresentation").changeSettingTo(DashboardTableBarPresentation[presentation]);
			this.budgetDashboardTaskTable.setPresentation(presentation);
			this.budgetDashboardUserTable.setPresentation(presentation);
		});

		this.tableTypeControl = ButtonRadioGroup.typed({
			value: BudgetDashboardType.HOURS,
			button: document.getElementById("dashboardTypeHoursButton")
		}, {
			value: BudgetDashboardType.REVENUE,
			button: document.getElementById("dashboardTypeRevenueButton")
		}, {
			value: BudgetDashboardType.COSTS,
			button: document.getElementById("dashboardTypeCostsButton")
		}).onValueChanged(type => {
			new UserSettingsChanger("budgetDashboardType").changeSettingTo(BudgetDashboardType[type]);
			this.setBudgetDashboardType(type);

			if (this.tableModeControl.getValue() !== BudgetDashboardMode.PERSONAL) {
				const selectedUserId = this.userCombobox.getSelectedItem()?.id;

				if (selectedUserId) {
					this.budgetDashboardTaskTable.toggleIsColumns(true);
				} else {
					this.budgetDashboardTaskTable.toggleIsColumns(false);
				}
			}
		});

		$("#budgetDashboardRoundingMode").change(() => this.updateBudgetDashboardTables());
		$("#budgetDashboardRoundToMinutes").change(() => this.updateBudgetDashboardTables());

		if ($("#budgetDashboardModeButtons").length !== 0) {
			const hasTaskSwitch = globals.isAdmin || config.isTaskLeader;
			if (!hasTaskSwitch) {
				Input.disable($("#budgetDashboardLeaderButton"));
			}

			this.tableModeControl = ButtonRadioGroup.typed({
				value: BudgetDashboardMode.PERSONAL,
				button: document.getElementById("budgetDashboardPersonalButton")
			}, {
				value: BudgetDashboardMode.TASK_LEADER,
				button: document.getElementById("budgetDashboardLeaderButton")
			}).onValueChanged(mode => {
				new UserSettingsChanger("budgetDashboardMode").changeSettingTo(BudgetDashboardMode[mode]);
				this.userCombobox.setDisabled(mode === BudgetDashboardMode.PERSONAL);
				if (mode === BudgetDashboardMode.PERSONAL) {
					this.userCombobox.setSelectedItemById(config.defaultUserId);
					this.budgetDashboardTaskTable.setType(BudgetDashboardType.HOURS, mode);
					this.setProjectLeaderControlsVisibility(false);
				} else {
					const userId = BrowserStorageSettings.getBudgetDashboardUserFilter(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD);
					if (userId) {
						this.userCombobox.setSelectedItemById(userId);
					} else {
						this.userCombobox.clearSelection();
					}

					this.budgetDashboardTaskTable.setType(this.tableTypeControl.getValue(), mode);
					this.reloadUsers(userId);
					this.setProjectLeaderControlsVisibility(true);
				}

				this.configureUserTableVisibility();
				BrowserStorageSettings.setSelectedTask(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD, null);
				this.taskDropdown.setSelectedTask()
				this.dashboardState.changeTask(0);
			});
			this.configureUserTableVisibility();

			if (!globals.showBudgetInformationForUsers && (globals.isAdmin || globals.isTaskLeader)) {
				this.tableModeControl.setValue(BudgetDashboardMode.TASK_LEADER);
				$("#budgetDashboardPersonalButton").prop("disabled", true);
			}
		}

		const showInactiveUsersForFilterCheckboxSelector = $("#budgetDashboardShowInactiveUsersForFilter");
		const showInactiveUsers = BrowserStorageSettings.getBudgetDashboardShowInactiveForFilter(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD);
		showInactiveUsersForFilterCheckboxSelector.prop("checked", showInactiveUsers !== null ? showInactiveUsers : false);
		showInactiveUsersForFilterCheckboxSelector.click(() => {
			const showInactiveForFilter = $("#budgetDashboardShowInactiveUsersForFilter").is(":checked");
			BrowserStorageSettings.setBudgetDashboardShowInactiveForFilter(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD, showInactiveForFilter);
			this.reloadUsers();
		});

		this.userCombobox = new Combobox<User>({
			items: [],
			formatter: user => {
				return user.fullname;
			},
			idElement: $("#budgetDashboardFilterUserId"),
			filterElement: $("#budgetDashboardFilterUserFilter"),
			caretElement: $("#budgetDashboardFilterUserCaret")
		});

		if (this.tableModeControl != null) {
			if (this.tableModeControl.getValue() === BudgetDashboardMode.PERSONAL) {
				this.userCombobox.setSelectedItemById(config.defaultUserId);
				this.userCombobox.setDisabled(true);
			} else {
				this.userCombobox.clearSelection();
			}
		}

		$(this.userCombobox).on(Combobox.EVENT_CHANGED, e => {
			const selectedUserId = this.userCombobox.getSelectedItem()?.id;
			if (this.tableModeControl.getValue() !== BudgetDashboardMode.PERSONAL) {
				if (selectedUserId) {
					this.budgetDashboardTaskTable.toggleIsColumns(true);
				} else {
					this.budgetDashboardTaskTable.toggleIsColumns(false);
				}

				BrowserStorageSettings.setBudgetDashboardUserFilter(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD, selectedUserId);
			}
			this.configureUserTableVisibility();
			this.updateBudgetDashboardTables();
		});

		const typeControlValue = this.tableTypeControl.getValue();
		const taskStatusFilterSelect = $("#taskStatusFilter");

		taskStatusFilterSelect.val(BrowserStorageSettings.getTaskStatusFilter(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD));

		taskStatusFilterSelect.on("change", () => {
			const taskStatusFilter = $("#taskStatusFilter").val() as TaskStatusFilter;
			BrowserStorageSettings.setTaskStatusFilter(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD, taskStatusFilter);

			if (taskStatusFilter !== TaskStatusFilter.ALL) {
				// When switching from INACTIVE/ACTIVE to ALL we need to clear the selected Task as we can't guarantee the selected Task is still present in the Task Dropdown
				BrowserStorageSettings.setSelectedTask(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD, null);
				this.taskDropdown.setSelectedTask();
				this.dashboardState.resetTask();
			} else {
				// When switching from ALL to INACTIVE/ACTIVE it's fine to keep the selected Task because it's a subset from ALL tasks
				this.dashboardState.changeTask(this.taskDropdown.getSelectedId());
			}

			this.taskDropdown.taskStatusFilter = taskStatusFilter;

			this.updateBudgetDashboardTables()
		});

		this.budgetDashboardTaskTable = new TaskTable({
			element: $("#budgetDashboardTaskTable"),
			initialFilter: this.buildFilterParams(),
			initialType: typeControlValue != null ? typeControlValue : BudgetDashboardType.HOURS,
			presentation: this.tablePresentationControl.getValue(),
			detailsPopover: true,
			onRowClick: (data: DashboardBudgetSumEntry) => {
				if (this.isTableRowClickable(data)) {
					this.dashboardState.changeTask(Number(data.id));
				}
			},
			isRowClickable: (data: DashboardBudgetSumEntry) => this.isTableRowClickable(data),
			orderable: true,
			clampNote: false,
			allowExpand: false,
			pinRecord: (task: DashboardBudgetSumEntry) => task.id === this.dashboardState.getTaskId()
		});

		this.budgetDashboardTaskTable.onDataChanged((taskInfo: BudgetDashboardTaskResponse) => {
			this.dashboardFilterPath.updateDashboardInfo(taskInfo);
			this.currentTaskExternalComments = taskInfo.taskExternalComments;
		});

		this.budgetDashboardUserTable = new UserTable({
			element: $("#budgetDashboardUserTable"),
			initialFilter: this.buildFilterParams(),
			initialType: this.tableTypeControl.getValue(),
			presentation: this.tablePresentationControl.getValue(),
			orderable: false,
			clampNote: false,
			allowExpand: false,
			customFooter: true
		});

		registerSortingFunctions(() => this.dashboardState.getTaskId());

		this.initReportsDialog();

		// init personal dashboard if the tableModeControl is not visible (when user views the Dashboard)
		if (!this.tableModeControl || this.tableModeControl.getValue() === BudgetDashboardMode.PERSONAL) {
			this.budgetDashboardTaskTable.setType(BudgetDashboardType.HOURS, BudgetDashboardMode.PERSONAL);
			this.setProjectLeaderControlsVisibility(false);
		} else {
			this.budgetDashboardTaskTable.setType(this.tableTypeControl.getValue(), BudgetDashboardMode.TASK_LEADER);
			this.setProjectLeaderControlsVisibility(true);
			this.reloadUsers(BrowserStorageSettings.getBudgetDashboardUserFilter(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD));
		}

		this.taskDropdown = new BudgetDashboardTaskDropdown({
				rootElement: $("#budgetDashboardFilterTaskDropdown"),
				taskStatusFilter: BrowserStorageSettings.getTaskStatusFilter(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD)
			}
		);

		if (selectedTask) {
			this.taskDropdown.setSelectedTask(selectedTask);
		}

		$(this.taskDropdown).on(TaskDropdown.EVENT_SELECTED, (e, task: Task) => {
			if (task && task.id) {
				BrowserStorageSettings.setSelectedTask(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD, task);
				this.dashboardState.changeTask(task.id)
			} else {
				BrowserStorageSettings.setSelectedTask(BudgetDashboardPage.GROUP_BUDGET_DASHBOARD, null);
				this.taskDropdown.setSelectedTask(null);
				this.dashboardState.changeTask(null)
			}
		});
	}

	private setProjectLeaderControlsVisibility(visible: boolean) {
		this.tableTypeControl.setVisible(visible);
		this.userCombobox.setVisible(visible);

		if (visible) {
			$("#budgetDashboardFilterUserIdLabel").show();
			$("#projectStatusReportDropdown").show();
			$("#userFilterContainer").show();
			$("#inactiveUsersContainer").show().css("visibility", "visible");
		} else {
			$("#budgetDashboardFilterUserIdLabel").hide();
			$("#projectStatusReportDropdown").hide();
			$("#userFilterContainer").hide();
			$("#inactiveUsersContainer").css("visibility", "hidden");
		}
	}

	private initReportsDialog() {
		const reportComment = $("#taskStatusReportComment");
		const reportDate = $("#taskStatusReportDataDate");

		let exportFileType: ExportType | null = null;

		const reportModal = new BootstrapModal.Modal({
			title: "selectTaskStatusReport.header",
			message: $("#selectTaskStatusReportDialog"),
			buttons: [{
				text: "dialog.create.report",
				css: "btn-primary",
				action: (modal, _button) => {
					const dataDate = parseDateInput(reportDate.val() as string);
					const params = [
						...this.buildFilterParams(),
						{name: "exportFileType", value: exportFileType},
						{name: "dataDate", value: getISODate(dataDate)},
						{name: "comment", value: reportComment.val() as string},
						{name: "timeZone", value: getCurrentISOOffset()},
						{name: "reportType", value: BudgetDashboardType[this.tableTypeControl.getValue()]},
						{
							name: "projectStatusReportType",
							value: $("#selectTaskStatusReportForm input[type='radio']:checked").val() as string
						}
					];

					modal.close();

					api("export/taskStatusReport.json")
						.post(params)
						.done(data => {
							if (data.exportJobId) {
								new DownloadStatus({
									exportJobId: data.exportJobId,
									exportType: exportFileType,
									loadingMessage: messages["downloadStatus.loadingMessage.budgetDashboard"],
									allowCancellation: true
								});
							}
						});
				}
			}, {
				text: "dialog.cancel",
			}]
		});

		$("#reportsDropdown a").click(event => {
			exportFileType = $(event.currentTarget).data("exportfiletype") as ExportType;

			reportComment.val(this.currentTaskExternalComments);
			reportDate.val(formatDate(Input.getLastMonthEnd()));

			$("#reportTypeRow").toggle(exportFileType !== ExportType.Excel);
			reportModal.show();
		});

		$("#setTaskStatusReportDataDateToLastMonth").click(() => {
			reportDate.val(formatDate(Input.getLastMonthEnd()));
		});

		$("#setTaskStatusReportDataDateToNow").click(() => {
			reportDate.val(formatDate(Temporal.Now.plainDateISO()));
		});

		$(".focusOnTaskStatusReportDataDateField").click(() => {
			$("#taskStatusReportDataDate").focus();
		});
	}

	private setBudgetDashboardType(type: BudgetDashboardType) {
		this.budgetDashboardTaskTable.setType(type);
		this.budgetDashboardUserTable.setType(type);
	}

	private configureUserTableVisibility() {
		const personalMode = this.tableModeControl.getValue() === BudgetDashboardMode.PERSONAL;
		const filteredByUser = !!$("#budgetDashboardFilterUserId").val();
		$("#budgetDashboardUserTable").parents(".panel").toggle(!personalMode && !filteredByUser);
	}

	private isTableRowClickable(data: DashboardBudgetSumEntry) {
		return data && data.id && Number(data.id) !== this.dashboardState.getTaskId();
	}

	private buildFilterParams(): RequestParam[] {
		const budgetDashboardMode = this.tableModeControl != null
			? BudgetDashboardMode[this.tableModeControl.getValue()]
			: BudgetDashboardMode[BudgetDashboardMode.PERSONAL];

		return [
			{name: "id", value: this.dashboardState.getTaskId()},
			{name: "userId", value: $("#budgetDashboardFilterUserId").val() as string},
			{name: "roundingMode", value: $("#budgetDashboardRoundingMode").val() as string},
			{name: "roundToMinutes", value: $("#budgetDashboardRoundToMinutes").val() as string},
			{name: "budgetDashboardMode", value: budgetDashboardMode},
			{name: "taskStatusFilter", value: $("#taskStatusFilter").val() as string},
		];
	}

	private updateBudgetDashboardTables() {
		if (this.budgetDashboardTaskTable) {
			this.budgetDashboardTaskTable.filter(this.buildFilterParams());
		}

		if (this.tableModeControl && this.tableModeControl.getValue() !== BudgetDashboardMode.PERSONAL && this.budgetDashboardUserTable) {
			this.budgetDashboardUserTable.filter(this.buildFilterParams());
		}
	}

	private reloadUsers(userId?: number) {
		// only load users if not Project Leader Dashboard
		if (this.tableModeControl && this.tableModeControl.getValue() !== BudgetDashboardMode.PERSONAL) {
			const showInactiveUsersForFilter = $("#budgetDashboardShowInactiveUsersForFilter").is(":checked");

			api("reports/filterDashboardUsers.json").get({
				taskId: this.dashboardState.getTaskId(),
				showInactiveForFilter: showInactiveUsersForFilter
			}).done((responseData: FilterUsersResponse) => {
				this.userCombobox.updateItems(responseData.users);
				if (userId) {
					this.userCombobox.setSelectedItemById(userId);
				}
				this.budgetDashboardTaskTable.toggleIsColumns(!!userId);
			});
		}
	}
}

interface BudgetDashboardTableConfig extends DashboardTableConfig<DashboardBudgetSumEntry> {
	initialType: BudgetDashboardType;
}

abstract class BudgetDashboardTable<
	TResponse extends BudgetDashboardTaskResponse | BudgetDashboardUserResponse
> extends DashboardTable<DashboardBudgetSumEntry, TResponse> {
	public static COLUMN_SIZE_DURATION = "120px";
	public static COLUMN_SIZE_BARS = "300px";
	protected static HOURS_TO_MS = 3600000;

	protected maxDuration: number;
	protected maxAmount: number;
	protected type: BudgetDashboardType;
	protected mode: BudgetDashboardMode;

	protected constructor(url: string, public config: BudgetDashboardTableConfig) {
		super(url, config);

		this.onDataChanged((json: TResponse) => {
			const results = this.getResults(json);
			const maxUserDuration = results.map(this.getRecordingDurationAnchor);
			this.maxDuration = Math.max(...maxUserDuration);
			const maxUserAmount = results.map(this.getRecordingAmountAnchor);
			this.maxAmount = Math.max(...maxUserAmount);
		});

		this.setType(config.initialType);
	}

	private getResults(json: TResponse): DashboardBudgetSumEntry[] {
		if ("tasks" in json) {
			return json.tasks.results;
		}
		if ("users" in json) {
			return json.users.results;
		}
		return [];
	}

	public setType(type: BudgetDashboardType, mode?: BudgetDashboardMode) {
		this.type = type;
		if (mode !== undefined) {
			this.mode = mode;
		}

		this.toggleIsColumns(this.mode !== BudgetDashboardMode.TASK_LEADER);
		this.dataTable.columns([3, 4, 7, 8]).visible(this.type === BudgetDashboardType.HOURS, false);
		this.dataTable.columns([1, 9, 10, 11, 13, 14]).visible(this.type === BudgetDashboardType.REVENUE, false);
		this.dataTable.columns([2, 15, 16, 17, 19, 20]).visible(this.type === BudgetDashboardType.COSTS, false);
		this.dataTable.columns.adjust().draw("page");
	}

	public setMode(mode: BudgetDashboardMode) {
		this.setType(this.type, mode);
	}

	protected renderBudgetInfo(this: void, hoursPlanned: number | undefined, _type: string, _recording: DashboardBudgetSumEntry, _meta: DataTables.CellMetaSettings) {
		return `<span class="label budget-info-chip budget-info-chip-text"><span>${hoursPlanned ? messages["report.budget.fixed"] : messages["report.budget.general"]}</span>`;
	}

	protected renderHoursPlanned(this: void, data: number | undefined, type: string, recording: DashboardBudgetSumEntry, _meta: DataTables.CellMetaSettings) {
		if (type === "sort" || type === "type") {
			return data;
		}

		if (recording.hoursPlanned || recording.hoursPlanned === 0) {
			return formatDuration(data * UserTable.HOURS_TO_MS);
		} else {
			return null;
		}
	}

	protected renderRemaining(this: void, data: number | undefined, type: string, recording: DashboardBudgetSumEntry, _meta: DataTables.CellMetaSettings) {
		if (type === "sort" || type === "type") {
			return data;
		}

		if (recording.hoursPlanned || recording.hoursPlanned === 0) {
			return formatDuration(data);
		} else {
			return "";
		}
	}

	protected renderCurrency(this: void, data: number, type: string, _recording: DashboardBudgetSumEntry, _meta: DataTables.CellMetaSettings) {
		if (type === "sort" || type === "type") {
			return data;
		}

		return formatCurrency(data);
	}

	protected renderCurrencyPlanned(this: void, data: number | undefined, type: string, recording: DashboardBudgetSumEntry, _meta: DataTables.CellMetaSettings) {
		if (type === "sort" || type === "type") {
			return data;
		}

		if (recording.budgetPlanned) {
			return formatCurrency(data);
		} else {
			return "";
		}
	}

	protected renderRemainingCurrency(this: void, data: number, type: string, recording: DashboardBudgetSumEntry, _meta: DataTables.CellMetaSettings) {
		if (type === "sort" || type === "type") {
			return data;
		}

		if (recording.budgetPlanned) {
			return formatCurrency(data);
		} else {
			if (data !== 0 && recording.hoursPlanned) {
				return `<span class="negative-remaining">${formatCurrency(data)}</span>`;
			} else {
				return "";
			}
		}
	}

	protected renderUsed(this: void, _data: void, _type: string, recording: DashboardBudgetSumEntry, _meta: DataTables.CellMetaSettings) {
		if (recording.hoursPlanned) {
			const msPlanned = recording.hoursPlanned * UserTable.HOURS_TO_MS;
			return formatPercent(recording.durationForBudget / msPlanned);
		} else {
			return "";
		}
	}

	protected renderDurationBar(_data: void, _type: string, recording: DashboardBudgetSumEntry, _meta: DataTables.CellMetaSettings) {
		const relativeRecordingAnchor = this.getRecordingDurationAnchor(recording);

		const durationAnchor = this.presentation === DashboardTableBarPresentation.Relative
			? relativeRecordingAnchor
			: this.maxDuration;

		const durationBillableOverBudget = recording.hoursPlanned !== 0
			? Math.max(recording.durationBillable - recording.hoursPlanned * UserTable.HOURS_TO_MS, 0)
			: 0;

		if (recording.duration === 0 && durationAnchor === 0) {
			return "";
		}

		const progressBars = [{
			css: "progress-bar-projectTime-billable",
			progress: (recording.durationBillable - durationBillableOverBudget) / durationAnchor
		}, {
			// draw green progress bar if no hours are assigned
			css: recording.hoursPlanned != null ? "progress-bar-workingTimeCategory-PAID_TIME_OFF" : "progress-bar-projectTime-billable",
			progress: durationBillableOverBudget / durationAnchor
		}, {
			css: "progress-bar-projectTime-notBillable",
			progress: recording.durationNotBillable / durationAnchor
		}];

		const barScale = recording.duration / durationAnchor;

		return renderProgress(progressBars, {
			size: "",
			barScale: barScale
		});
	}

	protected getRecordingDurationAnchor(this: void, recording: DashboardBudgetSumEntry): number {
		const msPlanned = (recording.hoursPlanned || 0) * UserTable.HOURS_TO_MS;
		return Math.max(recording.duration, msPlanned);
	}

	protected renderAmountUsed(this: void, _data: void, _type: string, recording: DashboardBudgetSumEntry, _meta: DataTables.CellMetaSettings) {
		if (recording.budgetPlanned) {
			return formatPercent(recording.amountForBudget / recording.budgetPlanned);
		} else {
			return "";
		}
	}

	protected renderAmountBar(_data: void, _type: string, recording: DashboardBudgetSumEntry, _meta: DataTables.CellMetaSettings) {
		const relativeRecordingAnchor = this.getRecordingAmountAnchor(recording);

		const durationAnchor = this.presentation === DashboardTableBarPresentation.Relative
			? relativeRecordingAnchor
			: this.maxAmount;

		const amountBillableOverBudget = recording.budgetPlanned !== 0
			? Math.max(recording.amountBillable - recording.budgetPlanned, 0)
			: 0;

		if (recording.amountBillable === 0 && durationAnchor === 0) {
			return "";
		}

		const progressBars = [{
			css: "progress-bar-projectTime-billable",
			progress: (recording.amountBillable - amountBillableOverBudget) / durationAnchor
		}, {
			css: "progress-bar-workingTimeCategory-PAID_TIME_OFF",
			progress: amountBillableOverBudget / durationAnchor
		}, {
			css: "progress-bar-projectTime-notBillable",
			progress: recording.amountNotBillable / durationAnchor
		}];

		const barScale = recording.amountBillable / durationAnchor;

		return renderProgress(progressBars, {
			size: "",
			barScale: barScale
		});
	}

	protected getRecordingAmountAnchor(this: void, recording: DashboardBudgetSumEntry): number {
		return Math.max(recording.amount, recording.budgetPlanned || 0);
	}

	protected renderAmountInternalUsed(this: void, _data: void, _type: string, recording: DashboardBudgetSumEntry, _meta: DataTables.CellMetaSettings) {
		if (recording.budgetPlanned) {
			return formatPercent(recording.amountInternal / recording.budgetPlanned);
		} else {
			return "";
		}
	}

	protected renderAmountInternalBar(_data: void, _type: string, recording: DashboardBudgetSumEntry, _meta: DataTables.CellMetaSettings) {
		const relativeRecordingAnchor = this.getRecordingAmountInternalAnchor(recording);

		const durationAnchor = this.presentation === DashboardTableBarPresentation.Relative
			? relativeRecordingAnchor
			: this.maxAmount;

		const amountInternalOverBudget = recording.budgetPlanned !== 0
			? Math.max(recording.amountInternal - recording.budgetPlanned, 0)
			: 0;

		if (recording.amountInternal === 0 && durationAnchor === 0) {
			return "";
		}

		const progressBars = [{
			css: "progress-bar-purple",
			progress: (recording.amountInternal - amountInternalOverBudget) / durationAnchor
		}, {
			css: "progress-bar-workingTimeCategory-PAID_TIME_OFF",
			progress: amountInternalOverBudget / durationAnchor
		}];

		const barScale = recording.amountInternal / durationAnchor;

		return renderProgress(progressBars, {
			size: "",
			barScale: barScale
		});
	}

	protected getRecordingAmountInternalAnchor(recording: DashboardBudgetSumEntry) {
		return Math.max(recording.amountInternal, recording.budgetPlanned || 0);
	}

	public toggleIsColumns(isPersonalDashboard: boolean) {
		if (this.type === BudgetDashboardType.HOURS) {
			this.dataTable.columns([5]).visible(!isPersonalDashboard, false);
			this.dataTable.columns([6]).visible(isPersonalDashboard, false);
		} else {
			this.dataTable.columns([5]).visible(false, false);
			this.dataTable.columns([6]).visible(false, false);
		}

		if (this.type === BudgetDashboardType.REVENUE) {
			this.dataTable.columns([11]).visible(!isPersonalDashboard, false);
			this.dataTable.columns([12]).visible(isPersonalDashboard, false);
		} else {
			this.dataTable.columns([11]).visible(false, false);
			this.dataTable.columns([12]).visible(false, false);
		}

		if (this.type === BudgetDashboardType.COSTS) {
			this.dataTable.columns([17]).visible(!isPersonalDashboard, false);
			this.dataTable.columns([18]).visible(isPersonalDashboard, false);
		} else {
			this.dataTable.columns([17]).visible(false, false);
			this.dataTable.columns([18]).visible(false, false);
		}

		const columnTitle = this.mode === BudgetDashboardMode.PERSONAL ? messages["report.column.myActual"] : messages["report.column.userActual"];
		$("#myActualHeaderHours").text(columnTitle);
		$("#myActualHeaderRevenue").text(columnTitle);
		$("#myActualHeaderCosts").text(columnTitle);
	}
}

class TaskTable extends BudgetDashboardTable<BudgetDashboardTaskResponse> {
	constructor(config: BudgetDashboardTableConfig) {
		super("/timr/reports/getTasksForBudgetDashboard.json", config);
	}

	protected getColumnSettings(): TypedColumnSettings<DashboardBudgetSumEntry>[] {
		return [
			{
				data: "description",
				className: "table-data-description",
				orderable: true,
				render: BasicDataTable.renderText,
				orderDataType: "task-dashboard-text"
			},
			{
				data: "hourlyRateDefinedForAllUsers",
				className: "hourly-rate-icon-column",
				orderable: false,
				render: this.renderHourlyRate
			},
			{
				data: "hourlyRateInternalDefinedForAllUsers",
				className: "hourly-rate-icon-column",
				orderable: false,
				render: this.renderHourlyRate
			},
			{
				data: "hoursPlanned",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: this.renderHoursPlanned,
				orderDataType: "task-dashboard-duration"
			},
			{
				data: "remaining",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: this.renderRemaining,
				orderDataType: "task-dashboard-duration"
			},
			{
				data: "durationForBudget",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: BasicDataTable.renderDuration,
				orderDataType: "task-dashboard-duration"
			},
			{
				data: "myDurationForBudget",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: BasicDataTable.renderDuration,
				orderDataType: "task-dashboard-duration"
			},
			{
				name: "progressDuration",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: false,
				render: this.renderUsed
			},
			{
				name: "progressDuration",
				width: BudgetDashboardTable.COLUMN_SIZE_BARS,
				orderable: false,
				render: (data: void, type: string, row: DashboardBudgetSumEntry, meta: DataTables.CellMetaSettings) => this.renderDurationBar(data, type, row, meta)
			},
			{
				data: "budgetPlanned",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: this.renderCurrencyPlanned,
				orderDataType: "task-dashboard-duration"
			},
			{
				data: "amountRemaining",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: this.renderRemainingCurrency,
				orderDataType: "task-dashboard-duration"
			},
			{
				data: "amountForBudget",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: this.renderCurrency,
				orderDataType: "task-dashboard-duration"
			},
			{
				data: "myAmountForBudget",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: this.renderCurrency,
				orderDataType: "task-dashboard-duration"
			},
			{
				name: "progressAmount",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: false,
				render: this.renderAmountUsed
			},
			{
				name: "progressAmount",
				width: BudgetDashboardTable.COLUMN_SIZE_BARS,
				orderable: false,
				render: (data: void, type: string, row: DashboardBudgetSumEntry, meta: DataTables.CellMetaSettings) => this.renderAmountBar(data, type, row, meta)
			},
			{
				data: "budgetPlanned",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: this.renderCurrencyPlanned,
				orderDataType: "task-dashboard-duration"
			},
			{
				data: "amountRemainingInternal",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: this.renderRemainingCurrency,
				orderDataType: "task-dashboard-duration"
			},
			{
				data: "amountInternal",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: this.renderCurrency,
				orderDataType: "task-dashboard-duration"
			},
			{
				data: "myAmountInternalForBudget",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: this.renderCurrency,
				orderDataType: "task-dashboard-duration"
			},
			{
				name: "progressAmountInternal",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: false,
				render: this.renderAmountInternalUsed,
				orderDataType: "task-dashboard-duration"
			},
			{
				name: "amountInternalBar",
				width: BudgetDashboardTable.COLUMN_SIZE_BARS,
				orderable: false,
				render: (data: void, type: string, row: DashboardBudgetSumEntry, meta: DataTables.CellMetaSettings) => this.renderAmountInternalBar(data, type, row, meta)
			}
		];
	}

	protected createDataTableConfig(): DataTables.Settings {
		return mergeProperties(super.createDataTableConfig(), {
			ajax: {
				dataSrc: "tasks.results"
			}
		});
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	protected createFooterDefinition(): FooterDefinition<BudgetDashboardTaskResponse> {
		return {
			3: json => json.tasks.total.hoursPlanned ? formatDuration(json.tasks.total.hoursPlanned * UserTable.HOURS_TO_MS) : "",
			4: json => json.tasks.total.hoursPlanned ? formatDuration(json.tasks.total.remaining) : "",
			5: json => formatDuration(json.tasks.total.durationForBudget),
			6: json => formatDuration(json.tasks.total.myDurationForBudget),

			9: json => formatCurrency(json.tasks.total.budgetPlanned),
			10: json => formatCurrency(json.tasks.total.amountRemaining),
			11: json => formatCurrency(json.tasks.total.amountForBudget),
			12: json => formatCurrency(json.tasks.total.myAmountForBudget),

			15: json => formatCurrency(json.tasks.total.budgetPlanned),
			16: json => formatCurrency(json.tasks.total.amountRemainingInternal),
			17: json => formatCurrency(json.tasks.total.amountInternal),
			18: json => formatCurrency(json.tasks.total.myAmountInternalForBudget)
		};
	}

	protected renderHourlyRate(this: void, data: boolean, _type: string, _recording: DashboardBudgetSumEntry, _meta: DataTables.CellMetaSettings) {
		return data ? "" : `<i class="far fa-exclamation-triangle text-warning" title="${messages["user.hourlyRate.unspecifiedForAtLeastOne"]}"></i>`;
	}
}

class UserTable extends BudgetDashboardTable<BudgetDashboardUserResponse> {

	constructor(config: BudgetDashboardTableConfig) {
		super("/timr/reports/getUsersForBudgetDashboard.json", config);
	}

	public setType(type: BudgetDashboardType) {
		this.type = type;
		this.dataTable.columns([4, 5, 6, 7, 8]).visible(this.type === BudgetDashboardType.HOURS, false);
		this.dataTable.columns([2, 9, 10, 11, 12, 13]).visible(this.type === BudgetDashboardType.REVENUE, false);
		this.dataTable.columns([3, 14, 15, 16, 17, 18]).visible(this.type === BudgetDashboardType.COSTS, false);
		this.dataTable.columns.adjust().draw("page");
	}

	protected getColumnSettings(): TypedColumnSettings<DashboardBudgetSumEntry>[] {
		return [
			{data: "description", className: "table-data-description", orderable: true, render: BasicDataTable.renderText},
			{
				data: "hoursPlanned",
				className: "slim-icon-column",
				orderable: false,
				render: this.renderBudgetInfo
			},
			{
				data: "showHourlyRateWarning",
				className: "hourly-rate-icon-column",
				orderable: false,
				render: this.renderHourlyRate
			},
			{
				data: "showHourlyRateInternalWarning",
				className: "hourly-rate-icon-column",
				orderable: false,
				render: this.renderHourlyRate
			},
			{
				data: "hoursPlanned",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: this.renderHoursPlanned
			},
			{
				data: "remaining",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: this.renderRemaining
			},
			{
				data: "durationForBudget",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: false,
				render: BasicDataTable.renderDuration
			},
			{
				name: "progressDuration",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: true,
				render: this.renderUsed
			},
			{
				name: "progressDuration",
				width: BudgetDashboardTable.COLUMN_SIZE_BARS,
				orderable: false,
				render: (data: void, type: string, row: DashboardBudgetSumEntry, meta: DataTables.CellMetaSettings) => this.renderDurationBar(data, type, row, meta)
			},
			{
				data: "budgetPlanned",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: false,
				render: this.renderCurrencyPlanned
			},
			{
				data: "amountRemaining",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: false,
				render: this.renderRemainingCurrency
			},
			{
				data: "amountForBudget",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: false,
				render: this.renderCurrency
			},
			{
				name: "amountUsed",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: false,
				render: this.renderAmountUsed
			},
			{
				name: "progressAmount",
				width: BudgetDashboardTable.COLUMN_SIZE_BARS,
				orderable: false,
				render: (data: void, type: string, row: DashboardBudgetSumEntry, meta: DataTables.CellMetaSettings) => this.renderAmountBar(data, type, row, meta)
			},
			{
				data: "budgetPlanned",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: false,
				render: this.renderCurrencyPlanned
			},
			{
				data: "amountRemainingInternal",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: false,
				render: this.renderRemainingCurrency
			},
			{
				data: "amountInternal",
				width: BudgetDashboardTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: false,
				render: this.renderCurrency
			},
			{
				name: "progressAmountInternal",
				width: UserTable.COLUMN_SIZE_DURATION,
				className: "text-right text-slim-dashboard",
				orderable: false,
				render: this.renderAmountInternalUsed
			},
			{
				name: "amountInternalBar",
				orderable: false,
				width: BudgetDashboardTable.COLUMN_SIZE_BARS,
				render: (data: void, type: string, row: DashboardBudgetSumEntry, meta: DataTables.CellMetaSettings) => this.renderAmountInternalBar(data, type, row, meta)
			}
		];
	}

	protected createDataTableConfig(): DataTables.Settings {
		return mergeProperties(super.createDataTableConfig(), {
			ajax: {
				dataSrc: "users.results"
			}
		});
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	protected createFooterDefinition(): FooterDefinition<BudgetDashboardUserResponse> {
		return {
			4: json => (json.users.total.hoursPlanned ? formatDuration(json.users.total.hoursPlanned * UserTable.HOURS_TO_MS) : "") + ";" +
				(json.users.total.hoursPlannedProjectBudget ? formatDuration(json.users.total.hoursPlannedProjectBudget * UserTable.HOURS_TO_MS) : ""),
			5: json => (json.users.total.hoursPlanned ? formatDuration(json.users.total.remaining) : "") + ";" +
				(json.users.total.hoursPlannedProjectBudget ? formatDuration(json.users.total.remainingProjectBudget) : ""),
			6: json => formatDuration(json.users.total.durationForBudget) + ";" + formatDuration(json.users.total.durationNotPlanned),

			9: json => formatCurrency(json.users.total.budgetPlanned) + ";" + formatCurrency(json.users.total.budgetPlannedProject),
			10: json => formatCurrency(json.users.total.amountRemaining) + ";" + formatCurrency(json.users.total.amountRemainingProjectBudget),
			11: json => formatCurrency(json.users.total.amountForBudget) + ";" + formatCurrency(json.users.total.amountForProjectBudget),

			14: json => formatCurrency(json.users.total.budgetPlanned) + ";" + formatCurrency(json.users.total.budgetPlannedProject),
			15: json => formatCurrency(json.users.total.amountRemainingInternal) + ";" + formatCurrency(json.users.total.amountRemainingInternalProjectBudget),
			16: json => formatCurrency(json.users.total.amountInternal) + ";" + formatCurrency(json.users.total.amountInternalProjectBudget)
		};
	}

	protected renderHourlyRate(this: void, showHourlyRateWarning: boolean, _type: string, _recording: DashboardBudgetSumEntry, _meta: DataTables.CellMetaSettings) {
		return showHourlyRateWarning ? `<i class="far fa-exclamation-triangle text-warning" title="${messages["user.hourlyRate.unspecified"]}"></i>` : "";
	}
}
